import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Landmark, Copy, CheckCircle2, Upload } from "lucide-react";
import { toast } from "sonner";

export default function BankTransferInstructions() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useLang();
  const [order, setOrder] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [proofFile, setProofFile] = useState(null);
  const [proofRef, setProofRef] = useState("");
  const [proofNote, setProofNote] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const [o, a] = await Promise.all([
      api.get(`/orders/${id}`),
      api.get("/settings/bank-accounts"),
    ]);
    setOrder(o.data);
    setAccounts(a.data.accounts || []);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t("bt.copied"));
  };

  const submitProof = async (e) => {
    e.preventDefault();
    if (!proofFile) { toast.error(t("bt.select_file")); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", proofFile);
      const { data: upload } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await api.post("/orders/proof", {
        order_id: id,
        proof_url: `${backendUrl}${upload.url}`,
        reference: proofRef, note: proofNote,
      });
      toast.success(t("bt.proof_sent"));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally { setUploading(false); }
  };





  if (!user) return <div className="p-12">{t("bt.connect")}</div>;
  if (!order) return <div className="p-12">{t("common.loading")}</div>;

  const hasProof = !!order.proof_url;
  const isConfirmed = order.payment_status === "paid";

  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid="bank-transfer-page">
      <h1 className="heading font-extrabold text-3xl mb-2">{t("bt.title")}</h1>
      <p className="text-[#4B5563] mb-8">{t("bt.order")} <span className="font-mono font-bold text-[#1A3322]" data-testid="order-reference">{order.reference}</span> · {t("bt.amount")} <span className="font-bold text-[#1A3322]" data-testid="order-total">{order.total.toFixed(2)} €</span></p>

      {isConfirmed && (
        <div className="bg-[#1A3322] text-white rounded-lg p-5 mb-6 flex items-center gap-3" data-testid="confirmed-banner">
          <CheckCircle2 className="w-6 h-6"/>
          <p className="font-semibold">{t("bt.confirmed_banner")}</p>
        </div>
      )}

      <div className="bg-white border border-[#1A3322]/10 rounded-lg p-6 mb-6">
        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-[#1A3322]/10">
          <img src="https://customer-assets-4nw71qhi.emergentagent.net/job_sukana-launch/artifacts/s0zrb57v_LOGO%20GNL%20SEC.jpg" alt="GNL" className="h-10 w-10 object-contain"/>
          <div className="flex-1">
            <p className="text-xs text-[#4B5563] uppercase tracking-wider">{t("bt.beneficiary")}</p>
            <p className="font-bold text-[#1A3322]">GNL SERVICES ET CONSULTING SARL</p>
          </div>
        </div>
        <h2 className="heading font-bold text-xl mb-4 flex items-center gap-2"><Landmark className="w-5 h-5"/> {t("bt.accounts")}</h2>
        <p className="text-sm text-[#4B5563] mb-4">{t("bt.transfer_instruction")} <strong className="text-[#C85A32]">{t("bt.reference_required_pre")} <span className="font-mono">{order.reference}</span> {t("bt.reference_required_post")}</strong></p>
        <div className="space-y-3" data-testid="bank-accounts-list">
          {accounts.map((a) => (
            <div key={`${a.bank}-${a.account_number}`} className="border border-[#1A3322]/10 rounded-lg p-4 bg-[#F5F5F0]/50" data-testid={`bank-${a.bank.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1">
                  <p className="font-bold text-[#1A3322]">{a.bank}</p>
                  <p className="text-xs text-[#4B5563] mt-1">{a.account_holder}</p>
                  <div className="mt-2 space-y-1 text-sm">
                    <p className="flex items-center gap-2"><span className="text-[#4B5563] w-20">{t("bt.account_number")}</span><span className="font-mono font-semibold">{a.account_number}</span><button onClick={() => copy(a.account_number)} className="text-[#C85A32]"><Copy className="w-3 h-3"/></button></p>
                    {a.iban && <p className="flex items-center gap-2"><span className="text-[#4B5563] w-20">{t("bt.iban")}</span><span className="font-mono">{a.iban}</span><button onClick={() => copy(a.iban)} className="text-[#C85A32]"><Copy className="w-3 h-3"/></button></p>}
                    {a.swift && <p className="flex items-center gap-2"><span className="text-[#4B5563] w-20">{t("bt.swift")}</span><span className="font-mono">{a.swift}</span></p>}
                    <p className="flex items-center gap-2"><span className="text-[#4B5563] w-20">{t("bt.currency")}</span><span className="font-semibold">{a.currency}</span></p>
                  </div>
                  {a.notes && <p className="text-xs text-[#4B5563] mt-2 italic">{a.notes}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {!hasProof && !isConfirmed && (
        <form onSubmit={submitProof} className="bg-white border border-[#1A3322]/10 rounded-lg p-6 space-y-4" data-testid="proof-form">
          <h2 className="heading font-bold text-xl flex items-center gap-2"><Upload className="w-5 h-5"/> {t("bt.send_proof_title")}</h2>
          <p className="text-sm text-[#4B5563]">{t("bt.send_proof_desc")}</p>
          <div className="space-y-2">
            <Label>{t("bt.file_label")}</Label>
            <Input type="file" accept="image/*,.pdf" onChange={(e) => setProofFile(e.target.files?.[0])} required data-testid="proof-file"/>
          </div>
          <div className="space-y-2">
            <Label>{t("bt.ref_optional")}</Label>
            <Input value={proofRef} onChange={(e) => setProofRef(e.target.value)} placeholder={t("bt.ref_placeholder")} data-testid="proof-reference"/>
          </div>
          <div className="space-y-2">
            <Label>{t("bt.note_optional")}</Label>
            <Textarea value={proofNote} onChange={(e) => setProofNote(e.target.value)} placeholder={t("bt.note_placeholder")} data-testid="proof-note"/>
          </div>
          <Button type="submit" disabled={uploading} className="pill-btn bg-[#1A3322] hover:bg-[#24472F] rounded-full h-11 px-6" data-testid="proof-submit">
            {uploading ? t("bt.sending") : t("bt.send_proof")}
          </Button>
        </form>
      )}

      {hasProof && !isConfirmed && (
        <div className="bg-[#D98A2C]/10 border border-[#D98A2C] rounded-lg p-6" data-testid="awaiting-confirmation">
          <p className="font-semibold text-[#C85A32]">{t("bt.awaiting_title")}</p>
          <p className="text-sm text-[#4B5563] mt-2">{t("bt.awaiting_desc")}</p>
          <Link to="/commandes" className="inline-block mt-4 text-[#1A3322] font-semibold underline">{t("bt.see_orders")}</Link>
        </div>
      )}
    </div>
  );
}
